import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, CheckCircle, Target, Zap, Star, Award, Users } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import CTABanner from '../components/CTABanner';
import useInView from '../components/useInView';

// Animated counter
const Counter = ({ target, suffix = '', inView }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const end = parseInt(target);
    if (isNaN(end)) { setCount(target); return; }
    const duration = 1500;
    const step = duration / end;
    const timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start >= end) clearInterval(timer);
    }, step);
    return () => clearInterval(timer);
  }, [inView, target]);
  return <>{typeof count === 'number' ? count : target}{suffix}</>;
};

const HomePage = () => {
  const { t } = useLang();
  const [heroVisible, setHeroVisible] = useState(false);
  const [aboutRef, aboutInView] = useInView();
  const [statsRef, statsInView] = useInView();

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const features = [
    { icon: <CheckCircle size={24} />, title: t('feature1_title'), desc: t('feature1_desc'), color: '#2E5AFF' },
    { icon: <Target size={24} />, title: t('feature2_title'), desc: t('feature2_desc'), color: '#8A2BE2' },
    { icon: <Award size={24} />, title: t('feature3_title'), desc: t('feature3_desc'), color: '#FF3B9A' },
  ];

  return (
    <div>
      {/* ─── HERO ─── */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        padding: '120px 24px 80px',
      }}>
        {/* Background hero orbs */}
        {[
          { x: '10%', y: '20%', size: 500, color: 'rgba(46,90,255,0.12)', anim: '10s' },
          { x: '70%', y: '10%', size: 400, color: 'rgba(138,43,226,0.1)', anim: '14s' },
          { x: '80%', y: '60%', size: 350, color: 'rgba(255,59,154,0.09)', anim: '9s' },
          { x: '30%', y: '70%', size: 300, color: 'rgba(46,90,255,0.07)', anim: '12s' },
        ].map((orb, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: orb.x,
              top: orb.y,
              width: orb.size,
              height: orb.size,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${orb.color}, transparent 70%)`,
              animation: `float ${orb.anim} ease-in-out infinite`,
              animationDelay: `${i * 2}s`,
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        ))}

        {/* Orbit rings decoration */}
        <div style={{
          position: 'absolute',
          right: '5%',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '500px',
          height: '500px',
          zIndex: 0,
          pointerEvents: 'none',
        }} className="hide-mobile">
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              position: 'absolute',
              inset: `${i * 40}px`,
              borderRadius: '50%',
              border: `1px solid rgba(${i === 0 ? '46,90,255' : i === 1 ? '138,43,226' : '255,59,154'}, ${0.25 - i * 0.06})`,
              animation: `spin-slow ${20 + i * 5}s linear infinite${i % 2 === 1 ? ' reverse' : ''}`,
            }}>
              {/* Orbiting dot */}
              <div style={{
                position: 'absolute',
                top: '-4px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: i === 0 ? '#2E5AFF' : i === 1 ? '#8A2BE2' : '#FF3B9A',
                boxShadow: `0 0 12px ${i === 0 ? '#2E5AFF' : i === 1 ? '#8A2BE2' : '#FF3B9A'}`,
              }} />
            </div>
          ))}
          {/* Center star */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '60px',
            height: '60px',
          }}>
            <Star size={60} style={{
              color: 'transparent',
              fill: 'url(#star-grad)',
              filter: 'drop-shadow(0 0 20px rgba(138,43,226,0.6))',
            }} />
            <svg style={{ position: 'absolute', width: 0, height: 0 }}>
              <defs>
                <linearGradient id="star-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#2E5AFF" />
                  <stop offset="100%" stopColor="#FF3B9A" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        {/* Hero content */}
        <div style={{ maxWidth: '1280px', margin: '0 auto', position: 'relative', zIndex: 1, width: '100%' }}>
          {/* Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 18px',
            borderRadius: '50px',
            background: 'rgba(46,90,255,0.12)',
            border: '1px solid rgba(46,90,255,0.3)',
            fontSize: '13px',
            fontWeight: 600,
            color: '#7ea4ff',
            marginBottom: '32px',
            opacity: heroVisible ? 1 : 0,
            transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease 0.1s',
          }}>
            <Zap size={14} />
            ГУАП · Санкт-Петербург · {new Date().getFullYear()}
          </div>

          {/* Main heading */}
          <h1 style={{
            fontSize: 'clamp(40px, 6vw, 80px)',
            fontWeight: 900,
            lineHeight: 1.08,
            letterSpacing: '-2px',
            marginBottom: '28px',
            maxWidth: '720px',
            opacity: heroVisible ? 1 : 0,
            transform: heroVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.7s ease 0.2s',
          }}>
            <span style={{ color: 'rgba(255,255,255,0.95)' }}>{t('hero_title1')}&nbsp;</span>
            <span style={{
              background: 'linear-gradient(135deg, #2E5AFF, #8A2BE2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>{t('hero_title2')}</span>
            <br />
            <span style={{
              background: 'linear-gradient(135deg, #8A2BE2, #FF3B9A)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>{t('hero_title3')}</span>
          </h1>

          {/* Subtitle */}
          <p style={{
            fontSize: 'clamp(15px, 2vw, 18px)',
            color: 'rgba(255,255,255,0.65)',
            lineHeight: 1.8,
            maxWidth: '580px',
            marginBottom: '44px',
            opacity: heroVisible ? 1 : 0,
            transform: heroVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.7s ease 0.35s',
          }}>
            {t('hero_sub')}
          </p>

          {/* CTA buttons */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            marginBottom: '64px',
            opacity: heroVisible ? 1 : 0,
            transform: heroVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.7s ease 0.5s',
          }}>
            <Link
              to="/program"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '16px 36px',
                background: 'linear-gradient(135deg, #2E5AFF, #8A2BE2, #FF3B9A)',
                color: '#fff',
                textDecoration: 'none',
                borderRadius: '50px',
                fontSize: '15px',
                fontWeight: 700,
                boxShadow: '0 16px 50px rgba(46,90,255,0.4)',
                transition: 'all 0.3s ease',
              }}
              onMouseOver={e => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 24px 60px rgba(46,90,255,0.5)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 16px 50px rgba(46,90,255,0.4)';
              }}
            >
              {t('hero_cta1')}
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/program"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '16px 36px',
                background: 'transparent',
                color: 'rgba(255,255,255,0.85)',
                textDecoration: 'none',
                borderRadius: '50px',
                fontSize: '15px',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.2)',
                transition: 'all 0.3s ease',
              }}
              onMouseOver={e => {
                e.currentTarget.style.borderColor = '#2E5AFF';
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.transform = 'translateY(-3px)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = 'rgba(255,255,255,0.85)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <BookOpen size={18} />
              {t('hero_cta2')}
            </Link>
          </div>

          {/* Stats */}
          <div
            ref={statsRef}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '16px',
              maxWidth: '700px',
              opacity: heroVisible ? 1 : 0,
              transform: heroVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.7s ease 0.65s',
            }}
          >
            {[
              { value: '5', label: t('stat1'), color: '#2E5AFF' },
              { value: '5', label: t('stat2'), color: '#8A2BE2' },
              { value: '1', label: t('stat3'), color: '#FF3B9A' },
              { value: 'ГОСТ', label: 'Р 54869-2011', color: '#2E5AFF', noCount: true },
            ].map((stat, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(18,22,36,0.7)',
                  backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '16px',
                  padding: '20px 16px',
                  borderTop: `2px solid ${stat.color}`,
                }}
              >
                <div style={{
                  fontSize: 'clamp(22px, 3vw, 30px)',
                  fontWeight: 900,
                  color: stat.color,
                  marginBottom: '4px',
                  letterSpacing: '-1px',
                }}>
                  {stat.noCount ? stat.value : <Counter target={stat.value} inView={statsInView} />}
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ABOUT ─── */}
      <section
        ref={aboutRef}
        style={{
          padding: '100px 24px',
          position: 'relative',
          zIndex: 1,
          opacity: aboutInView ? 1 : 0,
          transform: aboutInView ? 'translateY(0)' : 'translateY(50px)',
          transition: 'all 0.9s ease',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          {/* Section label */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
          }}>
            <div style={{ width: '40px', height: '2px', background: 'linear-gradient(90deg, #2E5AFF, #8A2BE2)' }} />
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#2E5AFF', letterSpacing: '3px', textTransform: 'uppercase' }}>
              {t('about_title')}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px', alignItems: 'start' }}>
            <div>
              <h2 style={{
                fontSize: 'clamp(28px, 4vw, 46px)',
                fontWeight: 800,
                color: '#fff',
                marginBottom: '24px',
                letterSpacing: '-1px',
                lineHeight: 1.15,
              }}>
                Познай{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #2E5AFF, #FF3B9A)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>проектную культуру</span>
              </h2>
              <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.8, marginBottom: '28px' }}>
                {t('about_desc')}
              </p>
              {/* Mission quote */}
              <blockquote style={{
                background: 'rgba(46,90,255,0.08)',
                border: '1px solid rgba(46,90,255,0.2)',
                borderLeft: '3px solid #2E5AFF',
                borderRadius: '0 12px 12px 0',
                padding: '20px 24px',
                marginBottom: '24px',
              }}>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.8, fontStyle: 'italic' }}>
                  {t('about_mission')}
                </p>
              </blockquote>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>
                {t('about_support')}
              </p>
            </div>

            {/* Feature cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {features.map((feat, i) => (
                <div
                  key={i}
                  className="hover-card"
                  style={{
                    background: 'rgba(18,22,36,0.7)',
                    backdropFilter: 'blur(16px)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '16px',
                    padding: '24px',
                    display: 'flex',
                    gap: '20px',
                    alignItems: 'flex-start',
                    animationDelay: `${i * 0.15}s`,
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: `${feat.color}1a`,
                    border: `1px solid ${feat.color}40`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: feat.color,
                    flexShrink: 0,
                  }}>
                    {feat.icon}
                  </div>
                  <div>
                    <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#fff', marginBottom: '6px' }}>
                      {feat.title}
                    </h3>
                    <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7 }}>
                      {feat.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <CTABanner />
    </div>
  );
};

export default HomePage;
